/**
 * Ghost-Seat-Generator v2 (BATTLE-19, A-CC35): REGELKONFORMER Bot-Run statt
 * Stat-Formel. Der Ghost ist das Board eines deterministischen Bot-Spielers, der
 * `stage` Runden mit IDENTISCHER Economy durchspielt (gleiche Shop-Regeln,
 * gleiches Gold, gleiche Stage-Odds, gleiche Deploy-Caps) — Gegner koennen per
 * Konstruktion nichts besitzen, was ein echter Spieler zu dieser Stage nicht
 * besitzen koennte. Vorlaeufer der echten Seats aus Stufe 3 (docs/09).
 *
 * Kaempfe werden im Ghost-Run NICHT simuliert (keine Rekursion): Rundenwechsel
 * nimmt alternierende Clean Wins an (A-CC35) — Einkommens-Annahme, kein Kampf.
 * `ghost.scalePerRoundPct` bleibt als Schwierigkeits-Drehregler (Default 0 = fair),
 * `ghost.pveScalePct` macht PvE-Runden weiterhin leichter.
 */
import type { TeamSetup } from '../sim/types'
import type { BalanceConfig } from '../config/balance'
import { advanceRound, newRun } from './runState'
import { botPlayShopPhase } from './bot'
import { buildPlayerTeam } from './roster'
import { confirmShop } from './shop'

export function generateGhostSeat(
  seed: number,
  stage: number,
  config: BalanceConfig,
  opts: { pve?: boolean } = {},
): TeamSetup {
  // eigener, unabhaengiger Run mit eigenem RNG-Stream (newRun cloned die Config)
  const state = newRun(seed >>> 0, config)
  for (let round = 1; round <= stage; round++) {
    botPlayShopPhase(state)
    if (round === stage) break
    // Rundenwechsel OHNE Kampf: confirmShop + GEMEINSAME advanceRound-Logik
    // (runState.ts) — Einkommen unter der konfigurierten Clean-Win-Annahme (A-CC35)
    confirmShop(state)
    const n = state.config.ghost.assumeCleanWinEvery
    advanceRound(state, { cleanWinLastRound: n > 0 && round % n === 0 })
  }

  const own = buildPlayerTeam(state)
  const g = config.grid
  // Schwierigkeits-Drehregler (Default 0 = regelkonform-fair); PvE relativ dazu
  const scaleBase = 100 + config.ghost.scalePerRoundPct * (stage - 1)
  const scale = opts.pve ? Math.floor((scaleBase * config.ghost.pveScalePct) / 100) : scaleBase
  // Punktspiegelung auf die Gegner-Haelfte (Spieler-Front row=rowsPerSide -> Ghost-Front row=rowsPerSide-1)
  return own.map((u) => ({
    ...u,
    hex: { col: g.cols - 1 - u.hex.col, row: g.rowsPerSide * 2 - 1 - u.hex.row },
    statPct: {
      ...u.statPct,
      hp: Math.floor(((u.statPct?.hp ?? 100) * scale) / 100),
      ad: Math.floor(((u.statPct?.ad ?? 100) * scale) / 100),
      ap: Math.floor(((u.statPct?.ap ?? 100) * scale) / 100),
    },
  }))
}
